/**
 * The console's Clients & pros contract (WALLIYO-0035): finding a customer,
 * their account page, and revealing their contact details. Part of the
 * console API (console-api.ts): same named responses, same errors
 * (ConsoleErrorResponse), same staff session.
 */

import type { SubscriptionStatus } from "./billing";
import type { SettingId } from "../settings/definitions";
import type { SettingCategory, SettingKind } from "../settings/types";
import type { SettingValue } from "../settings/value-types";
import type {
  ApprovalStatus,
  JobStatus,
  JobType,
  RateType,
  UserRole,
  VerificationStatus,
  WorkerCategory,
} from "../types";

// ── Clients & pros: customer accounts ─────────────────────────────────────
//
// Phone numbers and emails are always masked ("+224 6•• •• •• 41",
// "m•••@gmail.com"). Seeing them in full is a separate, audited request
// (reveal-contact) that needs its own feature and a reason.

/** Results per search page when `limit` is left out. */
export const ACCOUNT_SEARCH_DEFAULT_LIMIT = 20;
/** The most results one search page may hold. */
export const ACCOUNT_SEARCH_MAX_LIMIT = 50;
/** How many of a customer's jobs the account page shows. */
export const ACCOUNT_RECENT_JOBS_LIMIT = 20;

/** A customer's region: `gn` Guinea, `sn` Senegal, `us`, `eu`. */
export type CustomerRegion = "gn" | "sn" | "us" | "eu";

/**
 * GET /api/v1/console/accounts?query=…&limit=…&cursor=…
 *
 * Needs SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT (SEARCH_ACCOUNTS_STAFF_ACTION).
 * A plain read: not audited, but a refusal is.
 *
 * `query` is read as:
 * - an account id (a UUID): that account, deleted or not;
 * - an email when it contains `@`: case-insensitive, from the start of the
 *   address (`mariama.b@` finds `Mariama.Bah@gmail.com`);
 * - a phone number when it is only digits, spaces, `+`, `-`, `.` or brackets.
 *   Guinean numbers are normalised: `621 23 45 67`, `+224 621 23 45 67`,
 *   `00224 621234567` and `224621234567` all find `+224621234567`. Any other
 *   `+…` or `00…` number is matched as written. Anything shorter than a full
 *   number (at least 4 digits) matches anywhere in the number;
 * - otherwise a name: every word must appear in the first, middle or last
 *   name, ignoring case and accents (`fatoumata diallo` finds `Fatoumata
 *   Diallo`, `sekou` finds `Sékou`). Deleted accounts have no name and never
 *   match by name.
 */
export type SearchAccountsRequest = {
  /** 2 to 100 characters. */
  query: string;
  /** `nextCursor` from the previous page. Leave out for the first page. */
  cursor?: string;
  /** 1 to ACCOUNT_SEARCH_MAX_LIMIT. Defaults to ACCOUNT_SEARCH_DEFAULT_LIMIT. */
  limit?: number;
};

export type AccountSearchResult = {
  userId: string;
  /** "Fatoumata Diallo". Null for a deleted account, whose name was erased. */
  name: string | null;
  /** "+224 6•• •• •• 41". Null when there is none. */
  maskedPhone: string | null;
  /** "m•••@gmail.com". Null when there is none. */
  maskedEmail: string | null;
  primaryRole: UserRole;
  region: CustomerRegion;
  /** Their pro profile's trade and city, when they have one. */
  proProfile: { trade: WorkerCategory; city: string } | null;
  verificationStatus: VerificationStatus;
  deleted: boolean;
  createdAt: string;
};

/** Newest accounts first. */
export type SearchAccountsResponse = {
  /** How the query was read, so the console can say what it searched for. */
  matchedBy: "userId" | "email" | "phone" | "name";
  /** The full number searched for, when the query was one: "+224621234567". */
  normalisedPhone: string | null;
  results: readonly AccountSearchResult[];
  /** Pass as `cursor` for the next page. `null` on the last page. */
  nextCursor: string | null;
};

/**
 * Where a customer setting's value comes from. The most specific wins:
 *
 *   user → proProfile → allPros / allClients → region → appVersion → system → default
 *
 * - `clientProfile` is not resolved yet: there is no client_profiles table, so
 *   no value can be stored for one.
 * - `appVersion` applies only when the person's app version is known. The
 *   account page doesn't know it, so it never shows `appVersion` as a source.
 * - `allPros` applies only to someone with a pro profile. When `allPros` and
 *   `allClients` both apply and disagree, off wins: a restriction on one
 *   audience is never hidden by the other.
 */
export type CustomerSettingSource =
  | "user"
  | "proProfile"
  | "allPros"
  | "allClients"
  | "region"
  | "appVersion"
  | "system"
  | "default";

/** One setting or feature enablement as it applies to this person now. */
export type ResolvedCustomerSetting = {
  settingId: SettingId;
  kind: SettingKind;
  category: SettingCategory;
  value: SettingValue;
  source: CustomerSettingSource;
  /** Why it has this value, as given when it was set. Null for the default. */
  reason: string | null;
  /** When the value stops applying. Null means until changed. */
  expiresAt: string | null;
  /** When the value was last set. Null for the default. */
  updatedAt: string | null;
  /**
   * A feature switched off for this person specifically (`user` or
   * `proProfile`) with a reason, e.g. an interim suspension while a report is
   * looked into (ADR-0013 §5). A feature off for a whole region, audience or
   * everyone is a platform switch, not a restriction of this person.
   */
  restricted: boolean;
};

export type AccountJob = {
  jobId: string;
  status: JobStatus;
  type: JobType;
  trade: WorkerCategory;
  /** Which side of the job this person is on. */
  side: "client" | "pro";
  /** The pro on a client's job (null while none is chosen), or the client on a pro's. */
  otherParty: { userId: string; name: string | null } | null;
  createdAt: string;
};

/** Billing's statuses (ADR-0018 §3): pending, active, ending (renewal cancelled), suspended (unpaid, in grace), ended. */
export type WorkerSubscriptionStatus = SubscriptionStatus;

export type CustomerAccount = {
  identity: {
    userId: string;
    /** The name fields are null for a deleted account. */
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    name: string | null;
    maskedPhone: string | null;
    maskedEmail: string | null;
    primaryRole: UserRole;
    activeMode: "client" | "worker";
    region: CustomerRegion;
    verificationStatus: VerificationStatus;
    createdAt: string;
    deletedAt: string | null;
  };
  client: {
    jobsPosted: number;
    /**
     * Their latest sign of life: the latest job posted, message sent, quote
     * sent or review written. Null if there is none.
     */
    lastActivityAt: string | null;
  };
  proProfile: {
    proProfileId: string;
    trade: WorkerCategory;
    city: string;
    approvalStatus: ApprovalStatus;
    isVerified: boolean;
    ratingAvg: number;
    reviewCount: number;
    baseRate: number | null;
    rateType: RateType | null;
    /** Their latest subscription, whatever its status. Null if they never had one. */
    subscription: {
      /** The fee tier's name. */
      plan: string;
      status: WorkerSubscriptionStatus;
      provider: string;
      /** The end of the period paid for. */
      paidUntil: string | null;
    } | null;
  } | null;
  /**
   * Every active customer setting and feature enablement that applies to them:
   * all client ones (every account can post jobs), and pro ones only when they
   * have a pro profile.
   */
  settings: readonly ResolvedCustomerSetting[];
  /** True when any setting is `restricted`: the page shows "Suspension provisoire". */
  restricted: boolean;
  /** Their last ACCOUNT_RECENT_JOBS_LIMIT jobs, on either side, newest first. */
  recentJobs: readonly AccountJob[];
};

/**
 * GET /api/v1/console/accounts/:userId
 *
 * Needs VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT (VIEW_ACCOUNT_STAFF_ACTION).
 * A plain read: not audited, but a refusal is. An unknown id answers 404.
 */
export type GetCustomerAccountResponse = {
  account: CustomerAccount;
};

/**
 * POST /api/v1/console/accounts/:userId/reveal-contact
 *
 * Needs REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT
 * (REVEAL_CONTACT_DETAILS_STAFF_ACTION). A sensitive read, so every reveal is
 * audited with the customer as its subject: their own history shows who saw
 * their contact details, when, and why. The entry records which details were
 * shown, never the details themselves.
 *
 * A POST because it has a side effect: the audit entry. It asks for a reason
 * although sensitive reads don't strictly need one, because it is the
 * customer's own contact data and "why did you look?" is the first question
 * they will ask. It takes no Idempotency-Key: storing the answer for a replay
 * would copy the details into another table, and a repeated reveal is simply
 * audited twice.
 */
export type RevealContactDetailsRequest = {
  /** e.g. "Rappeler la cliente (D-0142)". 3 to 500 characters. */
  reason: string;
};

export type RevealContactDetailsResponse = {
  userId: string;
  /** In full, e.g. "+224621234541". Null when the account has none, or was deleted. */
  unmaskedPhone: string | null;
  unmaskedEmail: string | null;
  revealedAt: string;
};
